import React, { useEffect, useId, useRef, useState } from 'react';
import { Button } from './AdminUI';

export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'];
export const IMAGE_MAX_BYTES = 5 * 1024 * 1024; // keep in sync with the API limit

const formatBytes = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const isCloudinaryUrl = (url) => /(^|\.)res\.cloudinary\.com\//.test(url.replace(/^https?:\/\//, ''));

export function validateImageFile(file) {
  if (!IMAGE_TYPES.includes(file.type)) return 'Please choose a JPG, PNG, WEBP, GIF or AVIF image.';
  if (file.size > IMAGE_MAX_BYTES) return `This image is ${formatBytes(file.size)}. The maximum is 5 MB.`;
  return '';
}

/**
 * Product image picker: drag & drop or browse a file (uploaded to Cloudinary on save),
 * or paste an image link instead.
 *
 * value:    { file: File | null, url: string }  — `url` is the saved/pasted link
 * onChange: (nextValue) => void
 * progress: upload percentage while saving, otherwise null
 */
function ProductImageField({ value, onChange, progress = null, disabled = false, required = false }) {
  const inputId = useId();
  const inputRef = useRef(null);
  const [mode, setMode] = useState('upload');
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewFailed, setPreviewFailed] = useState(false);

  const { file, url } = value;

  // Local preview for a picked file, released when it changes
  useEffect(() => {
    if (!file) {
      setPreviewUrl('');
      return undefined;
    }
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  const shownImage = file ? previewUrl : url;
  useEffect(() => setPreviewFailed(false), [shownImage]);

  const pickFile = (picked) => {
    if (!picked) return;
    const problem = validateImageFile(picked);
    setError(problem);
    if (!problem) onChange({ file: picked, url });
  };

  const openBrowser = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    if (disabled) return;
    pickFile(event.dataTransfer.files?.[0]);
  };

  const removeImage = () => {
    setError('');
    onChange({ file: null, url: '' });
  };

  const switchMode = (next) => {
    setError('');
    setMode(next);
    // Picking a link discards a not-yet-uploaded file, and vice versa
    if (next === 'link' && file) onChange({ file: null, url: '' });
  };

  const uploading = progress !== null && Boolean(file);
  const hasImage = Boolean(shownImage);

  return (
    <div className="ax-upload">
      <div className="ax-upload__label">
        <span>
          Product image
          {required ? <span className="ax-field__req">*</span> : null}
        </span>
        <button
          type="button"
          className="ax-upload__switch"
          onClick={() => switchMode(mode === 'upload' ? 'link' : 'upload')}
          disabled={disabled}
        >
          <i className={`fas ${mode === 'upload' ? 'fa-link' : 'fa-cloud-arrow-up'}`} aria-hidden="true" />
          {mode === 'upload' ? 'Use an image link instead' : 'Upload a file instead'}
        </button>
      </div>

      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={IMAGE_TYPES.join(',')}
        className="ax-sr"
        tabIndex={-1}
        disabled={disabled}
        onChange={(event) => {
          pickFile(event.target.files?.[0]);
          event.target.value = ''; // allow picking the same file again
        }}
      />

      {mode === 'link' ? (
        <div className="ax-upload__link">
          <span className="ax-upload__link-thumb">
            {url && !previewFailed ? (
              <img src={url} alt="" onError={() => setPreviewFailed(true)} />
            ) : (
              <i className="fas fa-image" aria-hidden="true" />
            )}
          </span>
          <input
            className="ax-input"
            type="url"
            value={url}
            onChange={(event) => onChange({ file: null, url: event.target.value })}
            placeholder="https://example.com/product-image.jpg"
            aria-label="Image link"
            disabled={disabled}
          />
        </div>
      ) : hasImage ? (
        <div className={`ax-upload__preview${uploading ? ' is-uploading' : ''}`}>
          <div className="ax-upload__stage">
            {previewFailed ? (
              <span className="ax-upload__broken">
                <i className="fas fa-image" aria-hidden="true" />
                Image could not be loaded
              </span>
            ) : (
              <img src={shownImage} alt="Product preview" onError={() => setPreviewFailed(true)} />
            )}
            {uploading ? (
              <div className="ax-upload__progress" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
                <span>{progress < 100 ? `Uploading… ${progress}%` : 'Processing…'}</span>
                <span className="ax-upload__bar">
                  <span style={{ width: `${progress}%` }} />
                </span>
              </div>
            ) : null}
          </div>
          <div className="ax-upload__meta">
            <span className="ax-upload__file-icon">
              <i className={`fas ${file ? 'fa-file-image' : isCloudinaryUrl(url) ? 'fa-cloud' : 'fa-link'}`} aria-hidden="true" />
            </span>
            <div className="ax-upload__file">
              <strong title={file ? file.name : url}>{file ? file.name : 'Current image'}</strong>
              <small>
                {file
                  ? `${formatBytes(file.size)} · uploads when you save`
                  : isCloudinaryUrl(url)
                    ? 'Stored on Cloudinary'
                    : 'External image link'}
              </small>
            </div>
            <Button className="ax-btn--sm" icon="fa-arrow-up-from-bracket" onClick={openBrowser} disabled={disabled}>
              Replace
            </Button>
            <Button className="ax-btn--sm" variant="ghost" icon="fa-trash-can" onClick={removeImage} disabled={disabled}>
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <label
          htmlFor={inputId}
          className={`ax-upload__drop${dragging ? ' is-dragging' : ''}${error ? ' has-error' : ''}`}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openBrowser();
            }
          }}
          onDragOver={(event) => {
            event.preventDefault();
            if (!disabled) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          <span className="ax-upload__icon">
            <i className="fas fa-cloud-arrow-up" aria-hidden="true" />
          </span>
          <strong>{dragging ? 'Drop the image to add it' : 'Drag & drop an image here'}</strong>
          <span>
            or <u>browse your files</u>
          </span>
          <small>JPG, PNG, WEBP, GIF or AVIF · up to 5 MB · square images look best</small>
        </label>
      )}

      {error ? (
        <small className="ax-upload__error" role="alert">
          <i className="fas fa-circle-exclamation" aria-hidden="true" /> {error}
        </small>
      ) : null}
    </div>
  );
}

export default ProductImageField;
